import { RECENT_CONVERSATIONS_TITLE } from "@/language.constants";
import deriveShade from "@/utils/deriveShade";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { processTextWithEmojis } from "@/hooks/useEmojiWrapper";
dayjs.extend(relativeTime);

export default function RecentMessages({
  chatConfig,
  language,
  conversations,
  contactName,
  resumeConversation,
}) {
  const withOpacity = (color: string, alpha: number) => {
    if (!color) return color;
    if (color.startsWith("#")) {
      let hex = color.replace("#", "");
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((c) => c + c)
          .join("");
      }

      const bigint = parseInt(hex, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;

      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    if (color.startsWith("rgb(")) {
      return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
    }
    if (color.startsWith("rgba(")) {
      return color;
    }

    return color;
  };

  return (
    <div
      className={`cbb-recent-chats-wrapper flex max-h-[100%] w-full max-w-[360px] flex-col overflow-hidden rounded-[10px] shadow-[0_0_15px_0_#0000001A]`}
      style={{
        background: chatConfig?.chatBgColor,
      }}
    >
      <div
        className={`cbb-recent-chats-heading sticky top-0 w-full border-b p-[16px]`}
        style={{
          borderBottomColor: deriveShade(chatConfig?.chatBgColor),
          background: chatConfig?.chatBgColor,
          fontSize:
            parseInt(chatConfig?.greetingConfig?.typography?.size) + 3 + "px",
        }}
      >
        <span
          style={{
            fontSize:
              parseInt(chatConfig?.greetingConfig?.typography?.size) + 3 + "px",

            color: chatConfig?.greetingConfig?.typography?.color,
            background: `text ${chatConfig?.greetingConfig?.typography?.color}`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
          className="cbb-recent-chats-heading-text text-[16px] !font-medium"
        >
          {RECENT_CONVERSATIONS_TITLE[language?.toLowerCase()] ||
            RECENT_CONVERSATIONS_TITLE.english}
        </span>
      </div>
      <ul
        className="cbb-recent-chats-list-wrapper recent-msg-list new-scrollbar max-h-[360px] w-full overflow-auto p-[20px]"
        style={{ cursor: "pointer" }}
      >
        {conversations.map((conversation) => (
          <li
            key={conversation._id}
            style={{ borderBottomColor: deriveShade(chatConfig?.chatBgColor) }}
            className={`cbb-recent-chats-list-item grid grid-cols-[1fr_25px] items-center justify-between gap-[10px] border-b py-[10px] first:pt-0 last:border-b-0 last:pb-0`}
            onClick={() => resumeConversation(conversation._id)}
          >
            <div
              className={`cbb-recent-chats-details-wrapper grid gap-3 ${chatConfig?.showUserAvatar ? "grid-cols-[40px_1fr]" : "grid-cols-1"}`}
            >
              {chatConfig?.showUserAvatar && (
                <div className="cbb-recent-chats-profile profile">
                  <img
                    className="cbb-recent-chats-profile-image"
                    src={
                      chatConfig?.userAvatar?.isPreMade
                        ? preMadeUserAvatars[chatConfig?.userAvatar?.svgId - 1]
                        : chatConfig?.userAvatar?.url
                    }
                    alt="usericon"
                  />
                </div>
              )}
              <div className="cbb-recent-chats-details-content grid gap-[6px]">
                <span
                  style={{
                    fontSize:
                      chatConfig?.greetingConfig?.typography?.size + "px",
                    color: chatConfig?.greetingConfig?.typography?.color,
                    background: `text ${chatConfig?.greetingConfig?.typography?.color}`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                  className={`cbb-recent-chats-message line-clamp-3 w-full overflow-hidden text-ellipsis text-[13px] !font-medium`}
                >
                  {processTextWithEmojis(
                    conversation.lastMessage.userAnswer === "NO_REPLY"
                      ? conversation.lastMessage.nodeTitle
                      : conversation.lastMessage.userAnswer ||
                          conversation.lastMessage.nodeTitle,
                    "cbb-msg-emoji-inline",
                  )}
                </span>
                <div
                  className={`cbb-recent-chats-information fx items-center gap-[8px] whitespace-nowrap text-[12px] text-[#666666]`}
                >
                  <span
                    className="cbb-recent-chats-contact-name max-w-[110px] truncate"
                    style={{
                      fontSize:
                        parseInt(chatConfig?.greetingConfig?.typography?.size) -
                        1 +
                        "px",
                      color: chatConfig?.greetingConfig?.typography?.color,
                      background: `text ${chatConfig?.greetingConfig?.typography?.color}`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {contactName
                      ? contactName.length > 14
                        ? contactName.slice(0, 14) + "..."
                        : contactName
                      : "Visitor"}
                  </span>
                  <svg
                    className="cbb-recent-chats-information-dot"
                    width="4"
                    height="4"
                    viewBox="0 0 4 4"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="2.05371" cy="2" r="1.5" fill="#666666" />
                  </svg>
                  <span
                    className="cbb-recent-chats-time"
                    style={{
                      fontSize:
                        parseInt(chatConfig?.greetingConfig?.typography?.size) -
                        1 +
                        "px",
                      color: chatConfig?.greetingConfig?.typography?.color,
                      background: `text ${chatConfig?.greetingConfig?.typography?.color}`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      direction: "ltr",
                    }}
                  >
                    {dayjs(conversation.lastMessage.date).fromNow()}
                  </span>
                </div>
              </div>
            </div>
            <span className="cbb-recent-chats-open-button flex h-[24px] w-[24px] rounded-[100px] border border-[rgba(68,68,68,0.1)] bg-[#FAFAFA]">
              <svg
                className="cbb-recent-chats-open-button-arrow"
                width="5"
                height="8"
                viewBox="0 0 5 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.673891 0.166149C0.620338 0.219265 0.577832 0.282458 0.548824 0.352084C0.519817 0.42171 0.504882 0.49639 0.504882 0.571817C0.504882 0.647244 0.519817 0.721924 0.548824 0.79155C0.577832 0.861176 0.620338 0.924369 0.673891 0.977485L3.29074 3.59433C3.34429 3.64745 3.3868 3.71064 3.4158 3.78027C3.44481 3.84989 3.45975 3.92457 3.45975 4C3.45975 4.07543 3.44481 4.15011 3.4158 4.21973C3.3868 4.28936 3.34429 4.35255 3.29074 4.40567L0.673891 7.02251C0.620338 7.07563 0.577832 7.13882 0.548824 7.20845C0.519817 7.27808 0.504883 7.35276 0.504883 7.42818C0.504883 7.50361 0.519817 7.57829 0.548824 7.64792C0.577832 7.71754 0.620338 7.78074 0.673891 7.83385C0.780943 7.94027 0.925757 8 1.0767 8C1.22765 8 1.37246 7.94027 1.47951 7.83385L4.10207 5.21129C4.42307 4.8899 4.60337 4.45424 4.60337 4C4.60337 3.54576 4.42307 3.1101 4.10207 2.78871L1.47951 0.166149C1.37246 0.0597319 1.22765 -3.15931e-08 1.0767 -2.4995e-08C0.925756 -1.8397e-08 0.780943 0.0597319 0.673891 0.166149Z"
                  fill="#666666"
                />
              </svg>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const preMadeUserAvatars = [
  "https://dlvkyia8i4zmz.cloudfront.net/1JBvDAMgT2KgWxO8PvIi_1__1_.png",
  "https://dlvkyia8i4zmz.cloudfront.net/Nex2Ug4UQMGD75DzH2y5_2__1_.png",
  "https://dlvkyia8i4zmz.cloudfront.net/pxWqkGgTR5eEAEoguLXg_3__1_.png",
  "https://dlvkyia8i4zmz.cloudfront.net/lZQjg7omTQe8IGZ4nL8B_4__1_.png",
];
